"use client";

import { useEffect, useState } from "react";
import { ArrowUpRight, TrendingDown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  label: string;
  value: number;
  prefix: string;
  suffix: string;
  decimals: number;
  trend: string;
  trendDirection: string;
};
const metricsFr: Metric[] = [{
  label: "AÎNÉS ACCOMPAGNÉS",
  value: 450,
  prefix: "",
  suffix: "+",
  decimals: 0,
  trend: "+12% Annuel",
  trendDirection: "up"
}, {
  label: "REPAS DISTRIBUÉS",
  value: 12.5,
  prefix: "",
  suffix: "K",
  decimals: 1,
  trend: "+8.2% Mensuel",
  trendDirection: "up"
}, {
  label: "ISOLEMENT SOCIAL",
  value: 65,
  prefix: "-",
  suffix: "%",
  decimals: 0,
  trend: "-15% Réduction",
  trendDirection: "down"
}, {
  label: "HEURES DE BÉNÉVOLAT",
  value: 3.8,
  prefix: "",
  suffix: "K",
  decimals: 1,
  trend: "+22% Engagement",
  trendDirection: "up"
}];
const metricsEn: Metric[] = [{
  label: "SENIORS SUPPORTED",
  value: 450,
  prefix: "",
  suffix: "+",
  decimals: 0,
  trend: "+12% Annual",
  trendDirection: "up"
}, {
  label: "MEALS DISTRIBUTED",
  value: 12.5,
  prefix: "",
  suffix: "K",
  decimals: 1,
  trend: "+8.2% Monthly",
  trendDirection: "up"
}, {
  label: "SOCIAL ISOLATION",
  value: 65,
  prefix: "-",
  suffix: "%",
  decimals: 0,
  trend: "-15% Reduction",
  trendDirection: "down"
}, {
  label: "VOLUNTEER HOURS",
  value: 3.8,
  prefix: "",
  suffix: "K",
  decimals: 1,
  trend: "+22% Engagement",
  trendDirection: "up"
}];
const tickerItemsFr = [{
  label: "EDMONTON.FR",
  value: "ACTIF",
  change: "+100%",
  direction: "up"
}, {
  label: "INCLUSION.SOC",
  value: "92.4",
  change: "+4.12%",
  direction: "up"
}, {
  label: "AIDE.ALIM",
  value: "850kg",
  change: "+12.5%",
  direction: "up"
}, {
  label: "LIT.NUMERIQUE",
  value: "78%",
  change: "+15.3%",
  direction: "up"
}, {
  label: "ISOLEMENT.SENIOR",
  value: "BAS",
  change: "-22.1%",
  direction: "down"
}, {
  label: "BÉNÉVOLES.ACTIFS",
  value: "124",
  change: "+8.04%",
  direction: "up"
}, {
  label: "PROG.CULTUREL",
  value: "12/MOIS",
  change: "+2.6%",
  direction: "up"
}, {
  label: "FAMILLES.IMM",
  value: "215",
  change: "+14.2%",
  direction: "up"
}, {
  label: "FRANCOPHONIE.AB",
  value: "VIBRANTE",
  change: "+5.80%",
  direction: "up"
}, {
  label: "DIGNITÉ.AÎNÉS",
  value: "MAX",
  change: "+0.21%",
  direction: "up"
}];
const tickerItemsEn = [{
  label: "EDMONTON.FR",
  value: "ACTIVE",
  change: "+100%",
  direction: "up"
}, {
  label: "INCLUSION.SOC",
  value: "92.4",
  change: "+4.12%",
  direction: "up"
}, {
  label: "FOOD.AID",
  value: "850kg",
  change: "+12.5%",
  direction: "up"
}, {
  label: "DIG.LITERACY",
  value: "78%",
  change: "+15.3%",
  direction: "up"
}, {
  label: "SENIOR.ISOLATION",
  value: "LOW",
  change: "-22.1%",
  direction: "down"
}, {
  label: "ACTIVE.VOLUNTEERS",
  value: "124",
  change: "+8.04%",
  direction: "up"
}, {
  label: "CULT.PROGRAMS",
  value: "12/MONTH",
  change: "+2.6%",
  direction: "up"
}, {
  label: "IMM.FAMILIES",
  value: "215",
  change: "+14.2%",
  direction: "up"
}, {
  label: "FRANCOPHONIE.AB",
  value: "VIBRANT",
  change: "+5.80%",
  direction: "up"
}, {
  label: "SENIOR.DIGNITY",
  value: "MAX",
  change: "+0.21%",
  direction: "up"
}];
function CountUp({
  target,
  decimals,
  prefix,
  suffix,
  start,
  delay
}: {
  target: number;
  decimals: number;
  prefix: string;
  suffix: string;
  start: boolean;
  delay: number;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const duration = 1800;
    let startedAt = 0;
    const timer = window.setTimeout(() => {
      const step = (ts: number) => {
        if (!startedAt) startedAt = ts;
        const progress = Math.min((ts - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(target * eased);
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [start, target, delay]);
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return <span>
      {prefix}
      {formatted}
      {suffix}
    </span>;
}
export default function ChiffresClSDeNotreImpact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [language, setLanguage] = useState<string>("fr");
  useEffect(() => {
    const getStoredLanguage = () => {
      try {
        return localStorage.getItem("lacolonne_language") || "fr";
      } catch {
        return "fr";
      }
    };
    setLanguage(getStoredLanguage());
    const handleLanguageChange = () => {
      setLanguage(getStoredLanguage());
    };
    window.addEventListener("storage", handleLanguageChange);
    window.addEventListener("languagechange", handleLanguageChange);
    return () => {
      window.removeEventListener("storage", handleLanguageChange);
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);
  const isEnglish = language === "en";
  const metrics = isEnglish ? metricsEn : metricsFr;
  const tickerItems = isEnglish ? tickerItemsEn : tickerItemsFr;
  return <section data-section-id="3373" id="chiffres-clés-de-notre-impact" className="w-full bg-primary text-primary-foreground relative overflow-hidden py-20 md:py-28 lg:py-32">
      {/* Background subtle architectural grid */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} />

      <div ref={ref} className="relative mx-auto max-w-7xl px-6 lg:px-10">
        {/* Section Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs tracking-widest text-secondary uppercase mb-4">
              {isEnglish ? "// 02 — OUR IMPACT" : "// 02 — NOTRE IMPACT"}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-primary-foreground font-bold">
              {isEnglish ? <>
                  Pillars for <br className="hidden sm:inline" />
                  <span className="italic font-normal text-secondary">our community</span>
                </> : <>
                  Des piliers pour <br className="hidden sm:inline" />
                  <span className="italic font-normal text-secondary">notre communauté</span>
                </>}
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-between gap-6">
            <p className="text-sm sm:text-base text-primary-foreground/80 font-sans leading-relaxed">
              {isEnglish ? "Every number reflects a transformed life. We measure our success through the restored dignity of our seniors and the successful integration of francophone families in Edmonton." : "Chaque chiffre témoigne d'une vie transformée. Nous mesurons notre succès par la dignité retrouvée de nos aînés et l'intégration réussie des familles francophones à Edmonton."}
            </p>
            <Button asChild variant="ghost" className="group h-auto w-fit gap-3 px-0 py-2 font-mono text-xs tracking-widest text-primary-foreground hover:bg-transparent hover:text-secondary rounded-2xl">
              <Link to="#">
                {isEnglish ? "VIEW FULL REPORT" : "VOIR LE RAPPORT COMPLET"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Dramatically Enlarged Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 pt-8 border-t border-primary-foreground/15">
          {metrics.map((m, i) => {
          const down = m.trendDirection === "down";
          return <div key={`${m.label}-${language}`} data-index={i} className="flex flex-col justify-between" style={{
            transform: isVisible ? "translateY(0)" : "translateY(24px)",
            opacity: isVisible ? 1 : 0,
            transition: `opacity 800ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 100}ms, transform 800ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 100}ms`
          }}>
                {/* Massive bold statistic number */}
                <div className="font-serif font-bold text-6xl sm:text-7xl md:text-8xl lg:text-[5.5rem] xl:text-[6.5rem] leading-none tracking-tight text-primary-foreground tabular-nums">
                  <CountUp target={m.value} decimals={m.decimals} prefix={m.prefix} suffix={m.suffix} start={isVisible} delay={i * 120 + 150} />
                </div>

                {/* Label placed below in smaller text */}
                <div className="mt-4 font-mono text-xs sm:text-sm tracking-wider uppercase text-primary-foreground/80 font-medium">
                  {m.label}
                </div>

                {/* Subtle indicator without any red */}
                <div className="mt-3 flex items-center gap-1.5 font-mono text-xs text-primary-foreground/60">
                  {down ? <TrendingDown className="h-3.5 w-3.5 text-secondary" /> : <TrendingUp className="h-3.5 w-3.5 text-secondary" />}
                  <span>{m.trend}</span>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Full-width continuous ticker banner */}
      <div className="relative mt-16 md:mt-24 border-y border-primary-foreground/15 bg-primary-foreground/[0.03]">
        <div className="flex h-14 items-center overflow-hidden">
          <div className="flex shrink-0 items-center gap-10 whitespace-nowrap pr-10" style={{
          animation: "wvc-ticker-scroll 60s linear infinite"
        }}>
            {[...tickerItems, ...tickerItems, ...tickerItems].map((t, i) => {
            const down = t.direction === "down";
            return <div key={`${t.label}-${i}-${language}`} data-index={i} className="flex items-center gap-3 font-mono text-[10px] sm:text-xs tracking-widest uppercase text-primary-foreground/75">
                  <span className="text-primary-foreground/60">{t.label}</span>
                  <span className="text-primary-foreground font-semibold tabular-nums">{t.value}</span>
                  <span className={`inline-flex items-center gap-1 tabular-nums ${down ? "text-primary-foreground/80" : "text-secondary"}`}>
                    {down ? "▼" : "▲"} {t.change}
                  </span>
                  <span className="text-primary-foreground/20">|</span>
                </div>;
          })}
          </div>
        </div>
        <style>{`
          @keyframes wvc-ticker-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.3333%); }
          }
        `}</style>
      </div>
    </section>;
}